// characterAssetUrl — normalize character-asset URLs to the API proxy.
// Pure + dependency-free (no Vite/window) so it is unit-testable.
//
// The asset-manager's rig/texture UPLOAD responses return a raw S3 URL
// (https://s3-assets.avataros.xyz/splat-assets/character-assets/<id>/<file>).
// That bucket is NOT publicly readable — fetching it directly 403s. The same
// object is served, with credentials, through the API proxy at
// /api/character-assets/<id>/<file> (what a fresh character load and
// deriveMappingFromRig already use). After a Sync-to-Cloud the live rigPath
// briefly became the raw S3 URL, so the in-session avatar reload 403'd and the
// "Couldn't load this character" screen appeared until a hard refresh.
//
// This rewrites a raw character-asset URL to the proxied path. It rewrites only
// AAM-served hosts — staging S3 (s3-assets.avataros.xyz, which 403s) and the
// local MinIO/Garage dev store (localhost:9000, which has no CORS) — since both
// must be fetched through the credentialed API proxy. It is:
//   - idempotent (an already-proxied "/api/..." path is relative → returned as-is),
//   - conservative (blob:, data:, relative, and non-AAM absolute URLs untouched).
export function toProxiedAssetUrl(url) {
  if (typeof url !== 'string' || !/^https?:\/\//i.test(url)) return url
  const isAamHost = url.includes('s3-assets.avataros.xyz') ||
    /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\//i.test(url)
  if (!isAamHost) return url
  const i = url.indexOf('/character-assets/')
  return i >= 0 ? `/api${url.slice(i)}` : url
}

// rigDownloadTarget — resolve { url, filename } for downloading a character's
// rig GLB, or null when the character has no rig.
//
// The rig GLB cannot be fetched outside the app: a direct GET of the raw
// Garage/S3 URL 403s (measured). The web app is already authenticated, so the
// url here goes through toProxiedAssetUrl and a download fetches the SAME
// credentialed proxy path the runtime already loads from. Nothing here builds
// or derives an asset URL — it only rewrites the host the character row states.
//
// Splat / texavatars characters (eyeline-v10c, isaac-v6) carry an empty
// rigPath: they render from an /ogs pack, not a GLB, so there is no file to
// hand over. That returns null, and the caller must render the control SHOWN
// AND DISABLED with NO_RIG_REASON inline — never hidden, never a dead button.
// (The rule voiceInputState.js's header sets out; eyeFollowSupport.js is the
// recent precedent for the pure supported/unsupported decision.)
//
// Pure, so the decision is testable without a DOM.
export function rigDownloadTarget(character) {
  const rigPath = character?.rigPath
  if (typeof rigPath !== 'string' || rigPath.trim() === '') return null
  const slug = character?.id || character?.slug || 'character'
  return { url: toProxiedAssetUrl(rigPath), filename: `${slug}_rig.glb` }
}

/** Why the download control is disabled when rigDownloadTarget() returns null. */
export const NO_RIG_REASON =
  'This character has no rig GLB — it renders from a Gaussian-splat pack, so '
  + 'there is no rig file to download.'
